import {
  Flex,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Space,
  Text,
  useColorMode,
  Menu,
} from '@tonic-one/react';
import {
  CUSTOM_DATA_PERIOD_MAP,
  DATA_PERIOD_LIST,
  DATA_PERIOD_MAP,
  DataPeriod,
  IntervalType,
} from '@Utils/enums';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { AngleRightIcon } from '@tonic-one/react-icons';
import dayjs from 'dayjs';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { MonthPicker } from './monthPicker';
import { DayPicker } from './dayPicker';
import { DataPeriodState } from './type';
import { HourPicker } from './hourPicker';

const customArr = [IntervalType.Hour, IntervalType.Day, IntervalType.Month];

interface ReportDataPeriodProps {
  period: DataPeriodState;
  setPeriod: (v: DataPeriodState) => void;
}

function ReportDataPeriodSelector({
  period,
  setPeriod,
}: ReportDataPeriodProps) {
  const { offset } = useUtcOffsetStore();
  const [colorMode] = useColorMode();
  const { t } = useTranslation();
  const hoverBackgroundColor = {
    dark: 'rgba(255, 255, 255, 0.12)',
    light: 'rgba(0, 0, 0, 0.12)',
  }[colorMode];

  const setPreDefinedDate = (v: DataPeriod) => {
    period = {
      dataPeriod: v,
      timezoneOffset:
        v in [DataPeriod.Last6Months, DataPeriod.LastYear] ? '+00:00' : offset,
    };
    console.log('setPreDefinedDate: ', period);
    setPeriod(period);
  };
  const setCustomDate = (
    from: string,
    to: string,
    interval: IntervalType,
    timezoneOffset: string
  ) => {
    setPeriod({
      dataPeriod: 'custom',
      timeRange: {
        interval,
        gte: from,
        lte: to,
      },
      timezoneOffset,
    });
  };

  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <Menu minWidth="200px" isOpen={isOpen}>
        <MenuButton
          variant="secondary"
          minWidth="200px"
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          <Text>
            {period.dataPeriod !== 'custom'
              ? t(DATA_PERIOD_MAP[period.dataPeriod])
              : t(CUSTOM_DATA_PERIOD_MAP[period.timeRange.interval])}
          </Text>
        </MenuButton>
        <MenuList width={200}>
          <MenuGroup title={t('reportCommon.period.label.fixedPeriods')}>
            {DATA_PERIOD_LIST.map((v) => (
              <MenuItem
                key={v}
                pl={20}
                onClick={() => {
                  setPreDefinedDate(v);
                  setIsOpen(false);
                }}
              >
                {t(DATA_PERIOD_MAP[v])}
              </MenuItem>
            ))}
          </MenuGroup>
          <MenuGroup title={t('reportCommon.period.label.customPeriods')}>
            {customArr.map((v) => (
              <Flex
                key={v}
                cursor="pointer"
                pl="20px"
                pr=".75rem"
                py=".5rem"
                _hover={{ bg: hoverBackgroundColor }}
                onClick={() => {
                  const fromTo = getInitFromTo(v, offset);
                  setPeriod({
                    dataPeriod: 'custom',
                    timeRange: {
                      gte: fromTo.from,
                      lte: fromTo.to,
                      interval: v,
                    },
                    timezoneOffset:
                      v === IntervalType.Month ? '+00:00' : offset,
                  });
                }}
              >
                {t(CUSTOM_DATA_PERIOD_MAP[v])}
                <Space flex="1" />
                <AngleRightIcon />
              </Flex>
            ))}
            {/* <DateTimePicker /> */}
            {period.dataPeriod === 'custom' &&
              period.timeRange.interval === IntervalType.Hour && (
                <HourPicker
                  data={{
                    from: period.timeRange.gte,
                    to: period.timeRange.lte,
                  }}
                  setFromTo={(from, to) => {
                    setCustomDate(from, to, IntervalType.Hour, offset);
                  }}
                  onClose={() => {
                    setIsOpen(false);
                  }}
                />
              )}
            {period.dataPeriod === 'custom' &&
              period.timeRange.interval === IntervalType.Day && (
                <DayPicker
                  data={{
                    from: period.timeRange.gte,
                    to: period.timeRange.lte,
                  }}
                  setFromTo={(from, to) => {
                    setCustomDate(from, to, IntervalType.Day, offset);
                  }}
                  onClose={() => {
                    setIsOpen(false);
                  }}
                />
              )}
            {period.dataPeriod === 'custom' &&
              period.timeRange.interval === IntervalType.Month && (
                <MonthPicker
                  data={{
                    from: period.timeRange.gte,
                    to: period.timeRange.lte,
                  }}
                  setFromTo={(from, to) => {
                    setCustomDate(from, to, IntervalType.Month, '+00:00');
                  }}
                  onClose={() => {
                    setIsOpen(false);
                  }}
                />
              )}
          </MenuGroup>
        </MenuList>
      </Menu>
    </>
  );
}

const getInitFromTo = (type: IntervalType, offset: string) => {
  const now = dayjs().utc().utcOffset(offset);
  if (type === IntervalType.Month) {
    const lastMonth = now.add(-1, 'month').format('YYYY-MM');
    return {
      from: lastMonth,
      to: lastMonth,
    };
  } else if (type === IntervalType.Day) {
    const lastDayDate = now.add(-7, 'day');
    return {
      from: lastDayDate.format('YYYY-MM-DD'),
      to: now.format('YYYY-MM-DD'),
    };
  } else {
    // by hour, default set to last 2 h
    const lastDayDate = now.add(-2, 'hour');
    return {
      // hour is 00 - 23
      from: lastDayDate.format('YYYY-MM-DD HH'),
      to: now.format('YYYY-MM-DD HH'),
    };
  }
};

export default ReportDataPeriodSelector;
